import { useEffect, useState, useCallback, useRef } from "react";
import { fetchCurrentAppVersion } from "src/services/appVersion";

const POLL_MS = 5 * 60_000;

interface VersionCheckResult {
    updateAvailable: boolean;
    latestVersion: string | null;
}

/**
 * Polls the admin-published app version to detect a new release announced while
 * this tab was already open — a long-lived SPA tab never otherwise notices.
 *
 * Deliberately compares against whatever version THIS TAB first saw on mount
 * (captured once, in memory, never persisted) rather than a build-time constant —
 * an admin publish is independent of an actual code deploy, so there's no build-time
 * value that's guaranteed to ever match it again. Capturing per-mount also means a
 * freshly loaded/reloaded tab always starts caught up, with no stuck-banner risk.
 */
export function useVersionCheck(): VersionCheckResult {
    const baseline = useRef<string | null>(null);
    const baselineSet = useRef(false);
    const [latestVersion, setLatestVersion] = useState<string | null>(null);

    const check = useCallback(async () => {
        try {
            const info = await fetchCurrentAppVersion();
            const version = info?.version ?? null;
            if (!baselineSet.current) {
                baseline.current = version;
                baselineSet.current = true;
            }
            setLatestVersion(version);
        } catch {
            // Offline or transient network error — just try again next tick.
        }
    }, []);

    useEffect(() => {
        check();
        const tick = () => {
            if (document.visibilityState === "visible") check();
        };
        const id = setInterval(tick, POLL_MS);
        document.addEventListener("visibilitychange", tick);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", tick);
        };
    }, [check]);

    return {
        updateAvailable: baselineSet.current && latestVersion !== null && latestVersion !== baseline.current,
        latestVersion,
    };
}
